import React, { useState, useEffect } from 'react';

export type PlateStackForm = 'tilted' | 'flat';
export type PlateStackShape = 'rectangle' | 'diamond';
export type PlateStackDensity = 'minimal' | 'standard' | 'rich';
export type PlateStackOffset = 6 | 8 | 12 | 16;
export type PlateStackMotion = 'static' | 'assemble' | 'hover' | 'reveal' | 'reorder';

export interface PlateStackProps {
	// Core configuration
	form?: PlateStackForm; // 'tilted' (signature) or 'flat' (orthographic)
	shape?: PlateStackShape; // 'rectangle' or 'diamond'
	count?: 3 | 4 | 5; // plate count (default 4)
	density?: PlateStackDensity; // minimal/standard/rich
	offset?: PlateStackOffset; // 6/8/12/16px spacing system
	
	// Visual properties
	width?: number; // plate width (default 200)
	height?: number; // plate height (default 120)
	tiltDeg?: number; // 22-28° for tilted form (default 25)
	strokeWidth?: number; // hairline border (default 1)
	strokeColor?: string; // border color (default currentColor)
	fillOpacity?: number; // subtle fill 4-8% (default 0)
	fillColor?: string; // fill color (default currentColor)
	
	// Semantic labels (optional)
	labels?: string[]; // plate labels for semantic meaning
	
	// Motion & interaction
	motion?: PlateStackMotion; // motion behavior
	enableHover?: boolean; // hover lift effect
	onPlateClick?: (plateIndex: number) => void; // click handler
	
	// Layout
	className?: string;
	style?: React.CSSProperties;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const PlateStack: React.FC<PlateStackProps> = ({
	form = 'tilted',
	shape = 'rectangle',
	count = 4,
	density = 'standard',
	offset,
	width = 200,
	height = 120,
	tiltDeg = 25,
	strokeWidth = 1,
	strokeColor = 'currentColor',
	fillOpacity = 0,
	fillColor = 'currentColor',
	labels,
	motion = 'static',
	enableHover = true,
	onPlateClick,
	className,
	style,
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [hoveredPlate, setHoveredPlate] = useState<number | null>(null);

	// Determine offset based on density if not specified
	const plateOffset = offset || (density === 'minimal' ? 8 : density === 'rich' ? 16 : 12);
	
	// Clamp tilt to brand range
	const tilt = form === 'tilted' ? clamp(tiltDeg, 22, 28) : 0;
	
	// All plates same size (brand rule)
	const plateWidth = width;
	const plateHeight = height;
	
	// Build plate positions
	const plates = Array.from({ length: count }).map((_, i) => {
		if (form === 'flat') {
			// Flat stack: vertical offsets
			return {
				index: i,
				x: 0,
				y: i * plateOffset,
				label: labels?.[i] || `Plate ${i + 1}`,
			};
		} else {
			// Tilted stack: diagonal offsets
			return {
				index: i,
				x: i * plateOffset,
				y: -i * plateOffset, // move along diagonal
				label: labels?.[i] || `Plate ${i + 1}`,
			};
		}
	});

	// Motion effects
	useEffect(() => {
		if (motion === 'assemble') {
			const timer = setTimeout(() => setIsVisible(true), 100);
			return () => clearTimeout(timer);
		}
	}, [motion]);

	const getPlateStyle = (plateIndex: number): React.CSSProperties => {
		const isTop = plateIndex === count - 1;
		const isHovered = hoveredPlate === plateIndex;
		
		let transform = '';
		let transition = '';
		
		if (motion === 'assemble' && !isVisible) {
			// Initial misalignment for assemble motion
			transform = `translate(${plateOffset * 0.3}, ${-plateOffset * 0.3})`;
		}
		
		if (enableHover && isTop && isHovered) {
			// Hover lift effect
			transform += ` translate(0, -3px)`;
			transition = 'transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1)';
		}
		
		if (motion === 'reveal' && isTop) {
			// Reveal motion: top plate slides up
			transform += ` translate(0, -20px)`;
			transition = 'transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1)';
		}
		
		return {
			transform: transform.trim() || undefined,
			transition: transition || (motion === 'assemble' ? 'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)' : undefined),
			transitionDelay: motion === 'assemble' ? `${plateIndex * 60}ms` : undefined,
		};
	};

	// Calculate viewBox for proper cropping
	const padding = plateOffset * 2 + 20;
	const totalWidth = form === 'flat' ? plateWidth + padding * 2 : 
		Math.abs(Math.cos(tilt * Math.PI / 180) * (plateWidth + count * plateOffset)) + padding * 2;
	const totalHeight = form === 'flat' ? 
		plateHeight + (count - 1) * plateOffset + padding * 2 :
		Math.abs(Math.sin(tilt * Math.PI / 180) * (plateWidth + count * plateOffset)) + padding * 2;

	return (
		<div className={className} style={style}>
			<svg
				width={totalWidth}
				height={totalHeight}
				viewBox={`-${padding} -${padding} ${totalWidth} ${totalHeight}`}
				role="img"
				aria-label={`Plate Stack — ${form} form with ${count} layers`}
			>
				<g transform={form === 'tilted' ? `rotate(${tilt})` : undefined}>
					{plates.map((plate, i) => (
						<g
							key={plate.index}
							transform={`translate(${plate.x}, ${plate.y})`}
							style={{ ...getPlateStyle(i), cursor: onPlateClick ? 'pointer' : 'default' }}
							onMouseEnter={() => setHoveredPlate(i)}
							onMouseLeave={() => setHoveredPlate(null)}
							onClick={() => onPlateClick?.(i)}
						>
							{shape === 'diamond' ? (
								(() => {
									const s = Math.min(plateWidth, plateHeight) / Math.SQRT2;
									const cx = plateWidth / 2;
									const cy = plateHeight / 2;
									const x = cx - s / 2;
									const y = cy - s / 2;
									const r = Math.max(2, Math.min(10, s * 0.14));
									return (
										<rect
											x={x}
											y={y}
											width={s}
											height={s}
											rx={r}
											ry={r}
											fill={fillOpacity > 0 ? fillColor : 'none'}
											fillOpacity={fillOpacity}
											stroke={strokeColor}
											strokeWidth={strokeWidth}
											shapeRendering="crispEdges"
											strokeLinejoin="round"
											strokeLinecap="round"
											transform={`rotate(45 ${cx} ${cy})`}
										/>
									);
								})()
							) : (
								<rect
									width={plateWidth}
									height={plateHeight}
									fill={fillOpacity > 0 ? fillColor : 'none'}
									fillOpacity={fillOpacity}
									stroke={strokeColor}
									strokeWidth={strokeWidth}
									shapeRendering="crispEdges"
									rx={8}
									ry={8}
								/>
							)}
							{/* Optional label text */}
							{labels && labels[i] && (
								<text
									x={plateWidth / 2}
									y={plateHeight / 2}
									textAnchor="middle"
									dominantBaseline="middle"
									fontSize="12"
									fill={strokeColor}
									opacity={0.7}
								>
									{labels[i]}
								</text>
							)}
						</g>
					))}
				</g>
			</svg>
		</div>
	);
};
